import { useEffect, useState, useCallback } from 'react';
import { FileX2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { HeaderActions } from '@/components/layout/AppShell';
import { t } from '@/i18n';
import { useDashboardApi, useCompaniesApi, useProjectsApi } from '@/services/api';
import type {
  DashboardResponse,
  FinancialOverview as FinancialOverviewData,
  ProjectListItem,
} from '@ip/shared-types';
import { CostDetails } from './CostDetails';
import { DashboardSection } from './DashboardSection';
import { HealthSection } from './HealthSection';
import { PeopleSection } from './PeopleSection';
import { ProjectsSection } from './ProjectsSection';
import { TopCounterparties } from './TopCounterparties';

interface CompanyDashboardProps {
  companyNip: string;
  companyName: string;
  onCompanyNameLoad?: (name: string) => void;
}

/**
 * Skeletons, not a spinner: the shape of the page is known before the data
 * lands, so hold the layout instead of flashing an empty box.
 */
function DashboardSkeleton() {
  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-48" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          {Array.from({ length: 6 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-[380px]" />
        <div className="grid gap-4 xl:grid-cols-2">
          <Skeleton className="h-[300px]" />
          <Skeleton className="h-[300px]" />
        </div>
      </div>
      <div className="flex flex-col gap-4">
        <Skeleton className="h-6 w-32" />
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
        <Skeleton className="h-[380px]" />
      </div>
    </div>
  );
}

export function CompanyDashboard({
  companyNip,
  companyName,
  onCompanyNameLoad,
}: CompanyDashboardProps) {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [financial, setFinancial] = useState<FinancialOverviewData | null>(null);
  const [projects, setProjects] = useState<ProjectListItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const { getDashboard, getFinancialOverview } = useDashboardApi();
  const { getCompanyDetail } = useCompaniesApi();
  const { listProjects } = useProjectsApi();

  useEffect(() => {
    if (!companyName && companyNip && companyNip !== 'other' && onCompanyNameLoad) {
      getCompanyDetail(companyNip)
        .then((detail) => {
          if (detail.name) onCompanyNameLoad(detail.name);
        })
        .catch(() => {});
    }
  }, [companyNip, companyName, onCompanyNameLoad, getCompanyDetail]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // One shared `year` drives the dashboard endpoints. The financial
      // overview and the project list are BOTH optional — tag-restricted users
      // get a 403 from either (they are aggregates), which only hides that
      // section rather than failing the page.
      const [dashboardResult, financialResult, projectsResult] = await Promise.allSettled([
        getDashboard(companyNip, year),
        getFinancialOverview(companyNip, year),
        listProjects(companyNip),
      ]);

      if (dashboardResult.status === 'fulfilled') {
        setData(dashboardResult.value);
      } else {
        setData(null);
        toast.error(t('errors.loadingFailed'));
        console.error('Dashboard load failed:', dashboardResult.reason);
      }

      const financialData =
        financialResult.status === 'fulfilled' ? financialResult.value : null;
      setFinancial(financialData);
      if (financialResult.status === 'rejected') {
        console.warn('Financial overview unavailable:', financialResult.reason);
      }

      setProjects(projectsResult.status === 'fulfilled' ? projectsResult.value : null);
      if (projectsResult.status === 'rejected') {
        console.warn('Projects unavailable:', projectsResult.reason);
      }

      // Financial availableYears spans both directions (sales + purchases),
      // so prefer it for the selector; fall back to the cost dashboard's.
      const years = financialData?.availableYears.length
        ? financialData.availableYears
        : dashboardResult.status === 'fulfilled'
          ? dashboardResult.value.availableYears
          : [];
      if (years.length > 0 && !years.includes(year)) {
        setYear(Math.max(...years));
      }
    } finally {
      setLoading(false);
    }
  }, [companyNip, year, getDashboard, getFinancialOverview, listProjects]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const hasCostData = Boolean(data && data.summary.invoiceCount > 0);
  const hasFinancialData = Boolean(
    financial &&
      (financial.months.some(
        (m) => m.revenueNet !== 0 || m.costNet !== 0 || m.cashInNet !== 0 || m.cashOutNet !== 0,
      ) ||
        financial.kpis.receivablesTotal !== 0 ||
        financial.kpis.payablesTotal !== 0),
  );
  const hasData = hasCostData || hasFinancialData;
  const availableYears = financial?.availableYears.length
    ? financial.availableYears
    : (data?.availableYears ?? []);

  // `payroll` is null when the user may see the financial overview but not
  // salaries — a permission outcome, so the whole People band just isn't there.
  const payroll = financial?.payroll ?? null;

  const hasSales = Boolean(
    financial && (financial.kpis.revenueYtd !== 0 || financial.months.some((m) => m.revenueNet !== 0)),
  );

  return (
    <div className="flex flex-col gap-6">
      {availableYears.length > 0 && (
        <HeaderActions>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger size="sm" className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...availableYears]
                .sort((a, b) => b - a)
                .map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
        </HeaderActions>
      )}

      {loading && <DashboardSkeleton />}

      {!loading && !hasData && (
        <div className="text-muted-foreground flex flex-col items-center gap-3 py-20">
          <FileX2 className="size-10 opacity-50" />
          <p className="text-sm">{t('dashboard.noData')}</p>
        </div>
      )}

      {!loading && hasData && (
        <>
          {/* 1 — Kondycja spółki */}
          {hasFinancialData && financial && (
            <HealthSection
              data={financial}
              costDetails={hasCostData && data ? <CostDetails data={data} /> : undefined}
            />
          )}

          {/* Cost-only view (no financial access): the cost charts stand alone. */}
          {!hasFinancialData && hasCostData && data && (
            <DashboardSection
              title={t('dashboard.health.section')}
              subtitle={t('dashboard.health.sectionSubtitle')}
            >
              <CostDetails data={data} />
            </DashboardSection>
          )}

          {/* 2 — Ludzie */}
          {payroll && financial && (
            <PeopleSection
              payroll={payroll}
              financialMonths={financial.months}
              companyNip={companyNip}
            />
          )}

          {/* 3 — Projekty */}
          {projects && <ProjectsSection projects={projects} companyNip={companyNip} />}

          {/* 4 — Kontrahenci */}
          {hasFinancialData && financial && (
            <DashboardSection
              title={t('dashboard.counterparties.section')}
              subtitle={t('dashboard.counterparties.sectionSubtitle')}
            >
              <TopCounterparties
                topCustomers={financial.topCustomers}
                topVendors={financial.topVendors}
                showCustomers={hasSales && financial.topCustomers.length > 0}
              />
            </DashboardSection>
          )}
        </>
      )}
    </div>
  );
}
